/**
 * E002-T018: WorkflowService
 *
 * Service for managing workflows.
 * Derives state from WorkflowClientService.workflows$ (Single Source of Truth).
 * Schema cache is maintained separately (SRP).
 */

import { Service } from '@toeverything/infra';
import { LiveData } from '@toeverything/infra';

import type {
  WorkflowOwner,
  WorkflowSchema,
  WorkflowWithWebhook,
} from './workflow-client';
import { WorkflowClientService } from './workflow-client';

export interface WorkflowInfo {
  /** Unique workflow ID (workflow engine ID) */
  id: string;
  /** Display name of the workflow */
  name: string;
  /** Optional description */
  description?: string;
  /** Whether the workflow is active in the workflow engine */
  active: boolean;
  /** Whether the workflow has a webhook trigger */
  hasWebhook?: boolean;
  /** Webhook URL for triggering the workflow */
  webhookUrl?: string;
  /** Workflow categories for categorization */
  categories?: Array<{ id: string; name: string }>;
  /** Owner of the workflow (from WorkflowUserRole with role=Owner) */
  owner?: WorkflowOwner;
  previewSet?: unknown;
  /** Expected input fields (from webhook node) */
  inputFields?: WorkflowField[];
  /** Expected output fields (from response) */
  outputFields?: WorkflowField[];
}

// Re-export for convenience
export type { WorkflowSchema, WorkflowSchemaField } from './workflow-client';

export interface WorkflowField {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'date' | 'any';
  required?: boolean;
}

/** Compact ordered workflow projection persisted on automation blocks. */
export interface WorkflowStepSummary {
  id: string;
  name: string;
  type: string;
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * Convert the SWR definition into the small local-first projection required by
 * the canvas. Order is intentionally preserved.
 */
export function summarizeWorkflowSteps(
  steps: Array<Record<string, unknown>>
): WorkflowStepSummary[] {
  return steps.map((step, index) => {
    const type =
      nonEmptyString(step['type']) ??
      nonEmptyString(step['stepType']) ??
      nonEmptyString(step['nodeTypeSlug']) ??
      '';
    const id = nonEmptyString(step['id']) ?? `step-${index + 1}`;
    const name =
      (nonEmptyString(step['name']) ??
        nonEmptyString(step['label']) ??
        nonEmptyString(step['title']) ??
        nonEmptyString(step['displayName']) ??
        type) ||
      `Step ${index + 1}`;
    return { id, name, type };
  });
}

/**
 * Convert workflow engine workflow to WorkflowInfo
 */
function toWorkflowInfo(wf: WorkflowWithWebhook): WorkflowInfo {
  return {
    id: wf.id,
    name: wf.name,
    active: wf.active,
    hasWebhook: wf.hasWebhook,
    webhookUrl: wf.webhookUrl,
    categories: wf.categories ?? [],
    owner: wf.owner,
    previewSet: wf.previewSet,
  };
}

export class WorkflowService extends Service {
  constructor(private readonly workflowClient: WorkflowClientService) {
    super();

    // Single Source of Truth: derive workflows$ from WorkflowClientService cache.
    // Any mutation (rename, delete, create) on WorkflowClientService automatically
    // propagates to all subscribers (sidebar, selectors, etc.).
    this.workflowClient.workflows$.subscribe(engineWorkflows => {
      if (engineWorkflows.length === 0 && !this.workflowClient.isConfigured)
        return;
      this.workflows$.next(engineWorkflows.map(toWorkflowInfo));
    });

    // Initial load — triggers WorkflowClientService.workflows$.next() which flows
    // through the subscription above. No duplicate mapping.
    this.refreshWorkflows().catch(err => {
      console.error('[WorkflowService] Failed to load workflows:', err);
    });

    // sl-czam: Listen for targeted schema invalidations. Workflow consumers
    // explicitly refresh on entry and after mutations; an idle workspace must
    // not poll workflow list or health endpoints.
    this.onSchemaChanged = (event: { workflowId: string }) => {
      this.invalidateSchemaCache(event.workflowId);
      this.selectedWorkflowSchema$.next(null);
      this.loadWorkflowSchema(event.workflowId).catch(() => {});
    };
    (window as unknown as Record<string, unknown>)[
      '__workflow_schema_changed'
    ] = this.onSchemaChanged;

    this.onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && this.initialLoadDone) {
        this.refreshWorkflows().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.onWorkflowUpdated = () => {
      if (this.initialLoadDone) {
        this.refreshWorkflows().catch(() => {});
      }
    };
    window.addEventListener(
      'streamlain:workflow-updated',
      this.onWorkflowUpdated
    );
  }

  private readonly onVisibilityChange: () => void;
  private readonly onWorkflowUpdated: () => void;
  private readonly onSchemaChanged: (event: { workflowId: string }) => void;
  /** @internal */ initialLoadDone = false;

  override dispose(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener(
      'streamlain:workflow-updated',
      this.onWorkflowUpdated
    );
    // sl-czam: Unregister schema-changed callback
    delete (window as unknown as Record<string, unknown>)[
      '__workflow_schema_changed'
    ];
  }

  /** sl-za0v: SWR is always configured — no provisioning needed */
  readonly isEngineConfigured$ = new LiveData<boolean>(true);

  /** sl-1ahs.3.1: Backend connectivity state (online/offline) */
  readonly connectivity$ = new LiveData<boolean>(true);

  /** Loading state */
  readonly isLoading$ = new LiveData<boolean>(false);

  /** Error state */
  readonly error$ = new LiveData<string | null>(null);

  /** All available workflows (derived from WorkflowClientService) */
  readonly workflows$ = new LiveData<WorkflowInfo[]>([]);

  /** Only active workflows (ready to use) */
  readonly activeWorkflows$ = this.workflows$.map((workflows: WorkflowInfo[]) =>
    workflows.filter((wf: WorkflowInfo) => wf.active)
  );

  /** Only published workflows (ready to execute) */
  readonly executableWorkflows$ = this.workflows$.map(
    (workflows: WorkflowInfo[]) =>
      workflows.filter((wf: WorkflowInfo) => wf.active)
  );

  /** Workflows grouped by first category */
  readonly workflowsByCategory$ = this.workflows$.map(
    (workflows: WorkflowInfo[]) => {
      const groups = new Map<string, WorkflowInfo[]>();

      for (const wf of workflows) {
        const categoryName = wf.categories?.[0]?.name ?? 'other';
        const existing = groups.get(categoryName) ?? [];
        groups.set(categoryName, [...existing, wf]);
      }

      return Array.from(groups.entries()).map(([category, items]) => ({
        category,
        workflows: items,
      }));
    }
  );

  /** Get a specific workflow by ID */
  getWorkflow(id: string): WorkflowInfo | undefined {
    return this.workflows$.value.find((wf: WorkflowInfo) => wf.id === id);
  }

  // ===========================================================================
  // Schema Cache (SRP: separated from WorkflowInfo transport type)
  // ===========================================================================

  /** Schema cache per workflow ID */
  private readonly schemaCache = new Map<
    string,
    { schema: WorkflowSchema | null; loaded: boolean }
  >();

  /**
   * In-memory request cache for sidebar interactions. Canvas blocks never read
   * this map and never call the workflow API; they render their Y.Doc props.
   */
  private readonly workflowStepSummaryCache = new Map<
    string,
    WorkflowStepSummary[]
  >();
  private readonly workflowStepSummaryRequests = new Map<
    string,
    Promise<WorkflowStepSummary[]>
  >();

  async loadWorkflowStepSummaries(
    workflowId: string,
    force = false
  ): Promise<WorkflowStepSummary[]> {
    if (!force) {
      const cached = this.workflowStepSummaryCache.get(workflowId);
      if (cached) return cached;
      const pending = this.workflowStepSummaryRequests.get(workflowId);
      if (pending) return pending;
    }

    let request!: Promise<WorkflowStepSummary[]>;
    request = this.workflowClient
      .getWorkflowCurrent(workflowId)
      .then(current =>
        summarizeWorkflowSteps(current?.swrDefinition.steps ?? [])
      )
      .then(steps => {
        this.workflowStepSummaryCache.set(workflowId, steps);
        return steps;
      })
      .finally(() => {
        if (this.workflowStepSummaryRequests.get(workflowId) === request) {
          this.workflowStepSummaryRequests.delete(workflowId);
        }
      });
    this.workflowStepSummaryRequests.set(workflowId, request);
    return request;
  }

  invalidateWorkflowStepSummaries(workflowId: string): void {
    this.workflowStepSummaryCache.delete(workflowId);
  }

  /** Schema loading state per workflow */
  readonly schemaLoading$ = new LiveData<Map<string, boolean>>(new Map());

  /** Currently selected workflow's schema */
  readonly selectedWorkflowSchema$ = new LiveData<WorkflowSchema | null>(null);

  /**
   * Load schema for a specific workflow from the SWR backend endpoint.
   * Caches separately from WorkflowInfo (SRP).
   */
  async loadWorkflowSchema(workflowId: string): Promise<WorkflowSchema | null> {
    // Return from cache
    const cached = this.schemaCache.get(workflowId);
    if (cached?.loaded) {
      this.selectedWorkflowSchema$.next(cached.schema);
      return cached.schema;
    }

    // Mark as loading
    const loadingMap = new Map(this.schemaLoading$.value);
    loadingMap.set(workflowId, true);
    this.schemaLoading$.next(loadingMap);

    try {
      const schema = await this.workflowClient.fetchWorkflowSchema(workflowId);
      this.schemaCache.set(workflowId, { schema, loaded: true });
      this.selectedWorkflowSchema$.next(schema);
      return schema;
    } catch (error) {
      console.error(`[WorkflowService] Failed to load schema:`, error);
      // Don't cache failures — allows retry on next attempt
      return null;
    } finally {
      const loadingMap = new Map(this.schemaLoading$.value);
      loadingMap.set(workflowId, false);
      this.schemaLoading$.next(loadingMap);
    }
  }

  /** Check if schema is loading for a workflow */
  isSchemaLoading(workflowId: string): boolean {
    return this.schemaLoading$.value.get(workflowId) ?? false;
  }

  /** Invalidate cached schema (e.g. after node insertion changes the schema) */
  invalidateSchemaCache(workflowId: string): void {
    this.schemaCache.delete(workflowId);
  }

  /**
   * Trigger a full refresh from the workflow engine.
   * The subscription on WorkflowClientService.workflows$ handles the mapping —
   * this method only manages loading/error state and triggers the fetch.
   */
  async refreshWorkflows(): Promise<void> {
    // Only show loading indicator on initial load, not on background polls
    const showLoading = !this.initialLoadDone;
    if (showLoading) {
      this.isLoading$.next(true);
    }
    this.error$.next(null);

    try {
      await this.workflowClient.fetchWorkflows();
      this.isEngineConfigured$.next(this.workflowClient.isConfigured);
      this.connectivity$.next(true);
      this.initialLoadDone = true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('[WorkflowService] Failed to load workflows:', message);
      this.connectivity$.next(false);
      this.error$.next(message);
      this.workflows$.next([]);
    } finally {
      if (showLoading) {
        this.isLoading$.next(false);
      }
    }
  }
}
